import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertCircle, Check, CheckCircle2, ChevronDown,
  ChevronLeft, ChevronRight, Clock3, Database, Download, FileCheck2,
  ExternalLink, FileText, Loader2, Mail, Pencil, RefreshCw, Save,
  Send, UserRound, X,
} from 'lucide-react';

import {
  REPORT_REQUESTS_PAGE_LIMIT,
  loadReportDownload,
  loadReportJobs,
  loadReportOptions,
  loadReportRequest,
  loadReportRequests,
  rerunReportRequest,
  searchReportPatients,
  submitReportRequest,
  updateReportRequest,
} from './api';
import { errorMessage } from './ui';
import {
  Empty, Failed, Head, Loading, Pill, RailHeader, SearchField, WorkspaceTabs,
} from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { codeLabel } from './labels';

const PATIENT_SEARCH_MIN = 3;
const PATIENT_SEARCH_DELAY_MS = 250;
const EMPTY_REPORT_OPTIONS = Object.freeze({
  reportTypes: Object.freeze([]),
  companies: Object.freeze([]),
  languages: Object.freeze([]),
});
const EMPTY_REPORT_REQUESTS = Object.freeze({
  reportRequests: Object.freeze([]),
  total: 0,
  statusCounts: Object.freeze({ queued: 0, processing: 0, done: 0, failed: 0 }),
});

const STATUS_TABS = [
  { value: 'all', labelKey: 'ui.reports.status_all', Icon: FileText },
  { value: 'queued', labelKey: 'ui.reports.status_queued', Icon: Clock3 },
  { value: 'processing', labelKey: 'ui.reports.status_processing', Icon: Loader2 },
  { value: 'done', labelKey: 'ui.reports.status_ready', Icon: CheckCircle2 },
  { value: 'failed', labelKey: 'ui.reports.status_failed', Icon: AlertCircle },
];
const STATUS_VALUES = new Set(STATUS_TABS.map((tab) => tab.value));
// The rail's filter lives in ?status= so dashboard links can open a status directly.
const statusFromParams = (params) => {
  const value = String(params.get('status') || '').trim().toLowerCase();
  return STATUS_VALUES.has(value) ? value : 'all';
};

const STATUS_META = {
  queued: { labelKey: 'ui.reports.status_queued', tone: 'warn', icon: Clock3 },
  processing: { labelKey: 'ui.reports.status_processing', tone: 'info', icon: Loader2 },
  done: { labelKey: 'ui.reports.status_ready', tone: 'ok', icon: CheckCircle2 },
  failed: { labelKey: 'ui.reports.status_failed', tone: 'bad', icon: AlertCircle },
};

const EMPTY_PATIENT = { name: '', dateOfBirth: '', gender: '' };

function when(value) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function shortId(value) {
  const text = String(value || '');
  return text === '' ? '—' : text.split('-')[0].toUpperCase();
}

function bytes(value) {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return '—';
  if (size < 1024) return `${size} B`;
  const units = ['KB', 'MB', 'GB'];
  let amount = size / 1024;
  let index = 0;
  while (amount >= 1024 && index < units.length - 1) {
    amount /= 1024;
    index += 1;
  }
  return `${amount.toFixed(amount >= 100 ? 0 : 1)} ${units[index]}`;
}

function elapsed(start, finish) {
  const first = Number(start);
  const last = Number(finish);
  if (!Number.isFinite(first) || !Number.isFinite(last) || last < first) return null;
  const seconds = Math.round((last - first) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`;
}

function reportStatus(status) {
  const meta = STATUS_META[status];
  return meta
    ? { ...meta, label: tx(meta.labelKey) }
    : { label: tx('ui.common.unknown'), tone: 'flat', icon: FileText };
}

function parseRecipients(value) {
  const entries = String(value || '').split(/[\s,;]+/).map((email) => email.trim()).filter(Boolean);
  return [...new Set(entries.map((email) => email.toLowerCase()))];
}

function SelectMenu({ id, value, options, placeholder, disabled = false, onChange }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  const selected = options.find((option) => String(option.value) === String(value)) || null;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (root.current !== null && !root.current.contains(event.target)) setOpen(false);
    };
    const escape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <div className={`xo-report-select${open ? ' is-open' : ''}`} ref={root}>
      <button id={id} type="button" disabled={disabled} aria-haspopup="listbox"
        aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        <span className={selected === null ? 'is-placeholder' : ''}>
          <strong>{selected?.label || placeholder}</strong>
          {selected?.detail && <small>{selected.detail}</small>}
        </span>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-report-select-menu" role="listbox" aria-labelledby={id}>
          {options.map((option) => {
            const active = String(option.value) === String(value);
            return (
              <button key={String(option.value)} type="button" role="option"
                aria-selected={active} className={active ? 'is-selected' : ''}
                onClick={() => { onChange(option.value); setOpen(false); }}>
                <span><strong>{option.label}</strong>{option.detail && <small>{option.detail}</small>}</span>
                {active && <Check aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FormField({ label, error, hint, children }) {
  return (
    <label className={`xo-report-field${error ? ' is-error' : ''}`}>
      <span>{label}</span>
      {children}
      {error && <small className="is-error" role="alert">{error}</small>}
      {!error && hint && <small>{hint}</small>}
    </label>
  );
}

function PatientPicker({ patient, term, setTerm, matches, state, message, onChoose, onClear }) {
  if (patient !== null) {
    return (
      <div className="xo-report-patient-selected">
        <span aria-hidden="true"><UserRound /></span>
        <div>
          <strong>{patient.name || tx("ui.reports.patient_id_1dbd6c4a", { id: patient.id })}</strong>
          <small>{tx("ui.reports.patient_id_value_value2_892e638e", { id: patient.id, value: patient.subproject?.name ? ` · ${patient.subproject.name}` : '', value2: patient.dateOfBirth ? ` · ${patient.dateOfBirth}` : '' })}</small>
        </div>
        <button type="button" onClick={onClear}>{tx("ui.reports.change_64fbd995")}</button>
      </div>
    );
  }

  return (
    <div className="xo-report-patient-picker">
      <SearchField className="xo-report-patient-search" value={term} onChange={setTerm}
        autoComplete="off" spellCheck="false"
        placeholder={tx("ui.reports.search_patient_name_or_id_43dc0800")}
        label={tx("ui.reports.search_patient_name_or_id_43dc0800")}>
        {state === 'loading' && <Loader2 className="xo-spin" aria-hidden="true" />}
      </SearchField>
      {matches.length > 0 && (
        <div className="xo-report-patient-results xo-search-results" role="listbox" aria-label={tx("ui.reports.patient_matches_35ffa732")}>
          {matches.map((match) => (
            <button key={match.id} type="button" role="option" aria-selected="false"
              onClick={() => onChoose(match)}>
              <span aria-hidden="true"><UserRound /></span>
              <span>
                <strong>{match.name || tx("ui.reports.patient_id_1dbd6c4a", { id: match.id })}</strong>
                <small>{tx("ui.reports.patient_id_value_value2_892e638e", { id: match.id, value: match.subproject?.name ? ` · ${match.subproject.name}` : '', value2: match.dateOfBirth ? ` · ${match.dateOfBirth}` : '' })}</small>
              </span>
              <ChevronRight aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      {message && <p className={state === 'error' ? 'is-error' : ''}>{message}</p>}
    </div>
  );
}

// A step for the files the chosen reports are made from comes between the reports and the delivery, only when one of
// them asks for a file (the Acıbadem pharmacogenomics report's PharmCAT result).
function ReportWizardSteps({ step, withFiles = false }) {
  const steps = [
    { number: 1, label: tx("ui.reports.patient_de5c57ff"), detail: tx("ui.reports.details_and_samples_d07e89dd") },
    { number: 2, label: tx("ui.reports.reports_88bc3fe3"), detail: tx("ui.reports.reports_and_source_jobs_31ad9be7") },
    ...(withFiles ? [{ number: 3, label: tx('ui.reports.files_step'), detail: tx('ui.reports.files_step_detail') }] : []),
    { number: withFiles ? 4 : 3, label: tx("ui.reports.delivery_9631af52"), detail: tx("ui.reports.branding_and_recipients_2d2638a1") },
  ];
  return (
    <ol className="xo-report-wizard-steps" aria-label={withFiles
      ? tx('ui.reports.step_of_total', { step, total: steps.length })
      : tx("ui.reports.step_step_of_3_1bb44d50", { step: step })}>
      {steps.map((item) => (
        <li key={item.number} className={`${step === item.number ? 'is-current' : ''}${step > item.number ? ' is-complete' : ''}`}>
          <span aria-hidden="true">{step > item.number ? <Check /> : item.number}</span>
          <div><strong>{item.label}</strong><small>{item.detail}</small></div>
        </li>
      ))}
    </ol>
  );
}

// A chosen report (a type and one of its jobs), and the files its type asks for with a request.
const reportRowKey = (entry) => `${entry.type.id}:${entry.selectedJobId}`;
const reportInputs = (entry) => (Array.isArray(entry.type.inputs) ? entry.type.inputs : []);
// The kinds of file an input takes as people name them: ".json,application/json" is "JSON".
const acceptedKinds = (accept) => [...new Set(String(accept || '').split(',').map((part) => part.trim())
  .filter((part) => part.startsWith('.')).map((part) => part.slice(1).toUpperCase()))].join(', ') || String(accept || '');

// A file read in the browser as the backend takes it: no larger than the type allows, a JSON file that parses, and
// its bytes as base64; or what is wrong with it, in words for the person who chose it.
async function readReportFile(file, input) {
  if (file.size > input.maxBytes) {
    return { problem: tx('ui.reports.file_too_large', { name: input.label, size: bytes(input.maxBytes) }) };
  }
  const data = new Uint8Array(await file.arrayBuffer());
  if (input.format === 'json') {
    try { JSON.parse(new TextDecoder().decode(data)); } catch {
      return { problem: tx('ui.reports.file_not_json', { name: input.label }) };
    }
  }
  let binary = '';
  for (let index = 0; index < data.length; index += 0x8000) {
    binary += String.fromCharCode(...data.subarray(index, index + 0x8000));
  }
  return { file: { filename: file.name, bytes: file.size, mediaType: file.type || '', dataBase64: window.btoa(binary) } };
}

// One file a report asks for: its name, whether it is required, and a place to drop or choose it.
function ReportFileDrop({ input, file, error, onPick, onClear }) {
  const picker = useRef(null);
  const [over, setOver] = useState(false);
  const pick = (chosen) => { if (chosen) onPick(chosen); };
  return (
    <div className={`xo-report-file${file ? ' has-file' : ''}${error ? ' is-error' : ''}`}>
      <div className="xo-report-file-head">
        <strong>{input.label}</strong>
        <span className={input.required ? 'is-required' : ''}>{input.required ? tx('ui.reports.file_required') : tx('ui.reports.file_optional')}</span>
      </div>
      {input.description && <small className="xo-report-file-note">{input.description}</small>}
      <input ref={picker} type="file" accept={input.accept} hidden
        onChange={(event) => { pick(event.target.files?.[0]); event.target.value = ''; }} />
      {file ? (
        <div className="xo-report-file-chosen">
          <FileCheck2 aria-hidden="true" />
          <div><strong>{file.filename}</strong><small>{bytes(file.bytes)}</small></div>
          <button type="button" onClick={() => picker.current?.click()}>{tx('ui.reports.file_replace')}</button>
          <button type="button" onClick={onClear}>{tx('ui.reports.file_remove')}</button>
        </div>
      ) : (
        <button type="button" className={`xo-report-file-drop${over ? ' is-over' : ''}`}
          onClick={() => picker.current?.click()}
          onDragOver={(event) => { event.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={(event) => { event.preventDefault(); setOver(false); pick(event.dataTransfer.files?.[0]); }}>
          <FileText aria-hidden="true" />
          <span>{tx('ui.reports.file_choose')}</span>
          <small>{tx('ui.reports.file_accepts', { accept: acceptedKinds(input.accept), size: bytes(input.maxBytes) })}</small>
        </button>
      )}
      {error && <small className="xo-report-stage-error">{error}</small>}
    </div>
  );
}

function SampleInventory({ samples, loading, error, onRetry }) {
  return (
    <section className="xo-report-samples">
      <header>
        <div><h3>{tx("ui.reports.available_samples_11870f2d")}</h3><p>{tx("ui.reports.samples_with_a_completed_report_ready_analysis_3349eaa7")}</p></div>
        {!loading && !error && <span>{samples.length}</span>}
      </header>
      {loading && (
        <div className="xo-report-inventory-state"><Loader2 className="xo-spin" />{tx("ui.reports.checking_completed_analyses_4a48343f")}</div>
      )}
      {error && (
        <div className="xo-report-inventory-state is-error"><AlertCircle />{error}
          <button type="button" onClick={onRetry}>{tx("ui.reports.try_again_042c862e")}</button>
        </div>
      )}
      {!loading && !error && samples.length === 0 && (
        <div className="xo-report-inventory-state"><Database />{tx("ui.reports.no_report_ready_samples_are_available_for_this_patient_dbb7595c")}</div>
      )}
      {!loading && samples.length > 0 && (
        <div className="xo-report-sample-grid">
          {samples.map((sample) => (
            <article key={sample.key}>
              <span aria-hidden="true"><Database /></span>
              <div>
                <strong>{sample.name}</strong>
                <small>
                  {sample.visitNumber === null ? tx("ui.reports.visit_not_recorded_c97e50d9") : tx("ui.reports.visit_visitnumber_c5dee90c", { visitNumber: sample.visitNumber })}
                  {' · '}{sample.reportCount === 1
                    ? tx('ui.reports.one_report_type_available', { count: sample.reportCount })
                    : tx('ui.reports.many_report_types_available', { count: sample.reportCount })}
                </small>
              </div>
              <time>{when(sample.completedAt)}</time>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function selectedIds(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value === undefined || value === null || value === '') return [];
  return [String(value)];
}

function ReportChoice({ entry, selectedJobIds, onToggleJob }) {
  const selected = new Set(selectedJobIds.map(String));
  const available = entry.jobs.length > 0 && !entry.error;
  const pipelineName = entry.type.pipeline?.displayName || entry.type.pipeline?.name
    || tx('ui.reports.pipeline_unavailable');
  const availabilityLabel = entry.error ? tx('ui.reports.availability_check_failed')
    : entry.jobs.length === 0 ? tx('ui.reports.no_compatible_completed_job')
      : entry.jobs.length === 1
        ? tx('ui.reports.one_compatible_visit', { count: entry.jobs.length })
        : tx('ui.reports.many_compatible_visits', { count: entry.jobs.length });
  return (
    <article className={`xo-report-choice${selected.size > 0 ? ' is-selected' : ''}${available ? '' : ' is-unavailable'}`}>
      <header className="xo-report-choice-head">
        <span className="xo-report-choice-icon" aria-hidden="true"><FileText /></span>
        <span className="xo-report-choice-copy">
          <strong>{entry.type.displayName}</strong>
          <small>{pipelineName} · {availabilityLabel}</small>
        </span>
        {selected.size > 0 && <span className="xo-report-choice-count">{selected.size}</span>}
      </header>
      {available ? (
        <div className="xo-report-choice-visits" aria-label={tx("ui.reports.displayname_compatible_visits_87fb1777", { displayName: entry.type.displayName })}>
          {entry.jobs.map((job) => {
            const jobId = String(job.id);
            const isSelected = selected.has(jobId);
            const sampleName = job.upload?.sampleTypeName || job.upload?.sampleCode || tx('ui.reports.sample_58fabfa7');
            const visit = job.upload?.visitNumber === null
              ? tx('ui.reports.visit_not_recorded_c97e50d9')
              : tx('ui.reports.visit_number', { number: job.upload?.visitNumber });
            return (
              <button key={jobId} type="button" className={isSelected ? 'is-selected' : ''}
                aria-pressed={isSelected} onClick={() => onToggleJob(jobId)}>
                <span className="xo-report-choice-check" aria-hidden="true">{isSelected && <Check />}</span>
                <span>
                  <strong>{visit} · {sampleName}</strong>
                  <small>{tx("ui.reports.completed_value_job_id_1f583ab4", { value: when(job.completedAt), id: job.id })}</small>
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="xo-report-choice-empty">{availabilityLabel}</p>
      )}
    </article>
  );
}

function RequestCard({ request, onOpen }) {
  const meta = reportStatus(request.status);
  const Icon = meta.icon;
  const subject = request.patient?.name
    || request.job?.upload?.sampleTypeName
    || request.reportType?.displayName
    || tx('ui.reports.report_request_37e14d00');
  return (
    <button type="button" className={`xo-report-request is-${request.status}`} onClick={onOpen}>
      <span className="xo-report-request-icon" aria-hidden="true">
        <Icon className={request.status === 'processing' ? 'xo-spin' : ''} />
      </span>
      <span className="xo-report-request-copy">
        <span><strong>{request.reportType?.displayName || tx("ui.reports.report_ee45c303")}</strong><Pill tone={meta.tone}>{meta.label}</Pill></span>
        <small>{subject} · {request.company?.name || tx("ui.reports.unknown_company_3bd34a91")}</small>
        <small>{tx("ui.reports.value_job_value2_bfa15f3d", { value: when(request.requestedAt), value2: request.jobId || '—' })}</small>
      </span>
      <ChevronRight aria-hidden="true" />
    </button>
  );
}

export function RequestDetailModal({ request, options, onChanged, onClose }) {
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [action, setAction] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [preview, setPreview] = useState({ state: 'idle', url: '', message: '' });
  const current = detail === null ? request : { ...detail, ...request, recipients: detail.recipients };
  const meta = reportStatus(current.status);
  const StatusIcon = meta.icon;
  const companyChoices = useMemo(() => options.companies.map((company) => ({
    value: String(company.id), label: company.name, meta: company.code,
  })), [options.companies]);
  const languageChoices = useMemo(() => options.languages.map((language) => ({
    value: language.code, label: language.nativeName || language.name, meta: language.name,
  })), [options.languages]);
  const genderChoices = useMemo(() => [
    { value: '', label: tx("ui.reports.not_specified_60f1e913") },
    { value: 'female', label: tx("ui.reports.female_b7c17e97") },
    { value: 'male', label: tx("ui.reports.male_3f3a489c") },
    { value: 'other', label: tx("ui.reports.other_6e6a6f20") },
  ], []);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setDetailError('');
    loadReportRequest(request.uuid).then((result) => {
      if (!cancelled) setDetail(result);
    }).catch((error) => {
      if (!cancelled) setDetailError(errorMessage(error));
    });
    return () => { cancelled = true; };
  }, [request.uuid, request.status, request.reportSha256]);

  useEffect(() => {
    if (request.status !== 'done') {
      setPreview({ state: 'idle', url: '', message: '' });
      return undefined;
    }
    let cancelled = false;
    setPreview({ state: 'loading', url: '', message: '' });
    loadReportDownload(request.uuid).then((result) => {
      if (!cancelled) setPreview({ state: 'ready', url: result.url, message: '' });
    }).catch((error) => {
      if (!cancelled) setPreview({ state: 'error', url: '', message: errorMessage(error) });
    });
    return () => { cancelled = true; };
  }, [request.reportSha256, request.status, request.uuid]);

  useEffect(() => {
    const escape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [onClose]);

  async function download() {
    const tab = window.open('', '_blank');
    if (tab !== null) tab.opener = null;
    setDownloading(true);
    setDownloadError('');
    try {
      // Always request a fresh five-minute URL for an explicit open/download.
      // The preview URL may have expired while someone was reviewing the PDF.
      const result = await loadReportDownload(current.uuid);
      if (tab !== null) tab.location.replace(result.url);
      else {
        const link = document.createElement('a');
        link.href = result.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.click();
      }
    } catch (error) {
      if (tab !== null) tab.close();
      setDownloadError(errorMessage(error));
    } finally {
      setDownloading(false);
    }
  }

  function beginEdit() {
    setDraft({
      companyId: String(current.company?.id || ''),
      languageCode: current.language?.code || '',
      patientName: current.patient?.name || '',
      patientDateOfBirth: current.patient?.dateOfBirth || '',
      patientGender: current.patient?.gender || '',
    });
    setEditing(true);
    setActionError('');
    setActionNotice('');
  }

  function editPayload() {
    return {
      companyId: Number(draft.companyId),
      languageCode: draft.languageCode,
      patientName: draft.patientName.trim(),
      patientDateOfBirth: draft.patientDateOfBirth || null,
      patientGender: draft.patientGender || null,
    };
  }

  async function save() {
    setAction('save');
    setActionError('');
    setActionNotice('');
    try {
      const updated = await updateReportRequest(current.uuid, editPayload());
      setDetail(updated);
      setEditing(false);
      setActionNotice(tx("ui.reports.report_details_saved_0d228c9f"));
      onChanged(updated);
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setAction('');
    }
  }

  async function rerun(withEdits = false) {
    setAction(withEdits ? 'save-rerun' : 'rerun');
    setActionError('');
    setActionNotice('');
    try {
      const queued = await rerunReportRequest(current.uuid, withEdits ? editPayload() : {});
      setDetail(queued);
      setEditing(false);
      setActionNotice(tx("ui.reports.report_added_to_the_queue_14f9d08e"));
      onChanged(queued);
    } catch (error) {
      setActionError(errorMessage(error));
    } finally {
      setAction('');
    }
  }

  const timeline = [
    { key: 'requested', at: current.requestedAt, label: tx("ui.reports.requested_c26bf60f"), detail: tx("ui.reports.added_to_the_report_queue_78669d65") },
    current.startedAt === null ? null : {
      key: 'started', at: current.startedAt, label: tx("ui.reports.processing_started_4b47474e"),
      detail: elapsed(current.requestedAt, current.startedAt)
        ? tx("ui.reports.picked_up_after_value_7b86929a", { value: elapsed(current.requestedAt, current.startedAt) })
        : tx('ui.reports.picked_up_after_short_wait'),
    },
    current.completedAt === null ? null : {
      key: 'completed', at: current.completedAt,
      label: current.status === 'failed'
        ? tx('ui.reports.processing_failed') : tx('ui.reports.report_ready'),
      detail: current.startedAt === null
        ? tx('ui.reports.processing_finished')
        : tx('ui.reports.processed_in', { duration: elapsed(current.startedAt, current.completedAt) || '—' }),
    },
  ].filter(Boolean);

  return (
    <div className="xo-report-modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="xo-report-modal" role="dialog" aria-modal="true"
        aria-labelledby="report-request-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div className="xo-report-modal-title">
            <span className={`is-${current.status}`} aria-hidden="true">
              <StatusIcon className={current.status === 'processing' ? 'xo-spin' : ''} />
            </span>
            <div>
              <small>{tx("ui.reports.report_value_8734bafc", { value: shortId(current.uuid) })}</small>
              <h2 id="report-request-title">{current.reportType?.displayName || tx("ui.reports.report_request_37e14d00")}</h2>
              <p>{current.patient?.name || current.job?.upload?.sampleTypeName || tx("ui.reports.non_patient_analysis_e53ab927")}</p>
            </div>
          </div>
          <div className="xo-report-modal-actions">
            <Pill tone={meta.tone}>{meta.label}</Pill>
            {!editing && ['queued', 'failed', 'done'].includes(current.status) && (
              <button type="button" className="xo-report-modal-action" disabled={action !== ''}
                onClick={beginEdit}><Pencil />{current.status === 'done' ? tx("ui.reports.edit_rerun_e1c666c7") : tx("ui.reports.edit_details_4173a96a")}</button>
            )}
            {!editing && ['failed', 'done'].includes(current.status) && (
              <button type="button" className="xo-report-modal-action is-primary" disabled={action !== ''}
                onClick={() => rerun(false)}>
                {action === 'rerun' ? <Loader2 className="xo-spin" /> : <RefreshCw />}
                {action === 'rerun' ? tx("ui.reports.queuing_90d650a9") : tx("ui.reports.rerun_afd8524d")}
              </button>
            )}
            <button type="button" className="xo-icon-btn" aria-label={tx("ui.reports.close_report_details_4ec73968")} onClick={onClose}><X /></button>
          </div>
        </header>

        <div className="xo-report-modal-body">
          <main className="xo-report-modal-details">
            {detailError && <p className="xo-report-inline-error" role="alert">{detailError}</p>}
            {actionError && <p className="xo-report-notice is-error" role="alert"><AlertCircle />{actionError}</p>}
            {actionNotice && <p className="xo-report-notice is-success" role="status"><CheckCircle2 />{actionNotice}</p>}

            <section className="xo-report-detail-section">
              <header><h3>{tx("ui.reports.report_details_387f34d9")}</h3><span>{tx("ui.reports.request_and_source_1908bd4e")}</span></header>
              {editing ? (
                <div className="xo-report-edit-grid">
                  <FormField label={tx("ui.reports.company_7a199499")}>
                    <SelectMenu id="edit-report-company" value={draft.companyId}
                      options={companyChoices} placeholder={tx("ui.reports.choose_company_14b2f441")}
                      onChange={(value) => setDraft((currentDraft) => ({ ...currentDraft, companyId: String(value) }))} />
                  </FormField>
                  <FormField label={tx("ui.reports.language_89b86ab0")}>
                    <SelectMenu id="edit-report-language" value={draft.languageCode}
                      options={languageChoices} placeholder={tx("ui.reports.choose_language_94d0c90d")}
                      onChange={(value) => setDraft((currentDraft) => ({ ...currentDraft, languageCode: value }))} />
                  </FormField>
                </div>
              ) : (
                <dl className="xo-report-facts">
                  <div><dt>{tx("ui.reports.company_7a199499")}</dt><dd>{current.company?.name || '—'}</dd></div>
                  <div><dt>{tx("ui.reports.language_89b86ab0")}</dt><dd>{current.language?.nativeName || current.language?.name || '—'}</dd></div>
                  <div><dt>{tx("ui.reports.pipeline_32b1d5a7")}</dt><dd>{current.reportType?.pipeline?.displayName || current.reportType?.pipeline?.name || '—'}</dd></div>
                  <div><dt>{tx("ui.reports.pipeline_job_8dd21e95")}</dt><dd>#{current.jobId || '—'} · {shortId(current.job?.uuid)}</dd></div>
                  <div><dt>{tx("ui.reports.sample_58fabfa7")}</dt><dd>{current.job?.upload?.sampleTypeName || current.job?.upload?.sampleCode || '—'}</dd></div>
                  <div><dt>{tx("ui.reports.visit_7c9a8664")}</dt><dd>{current.job?.upload?.visitNumber ?? '—'}</dd></div>
                </dl>
              )}
            </section>

            {(current.patient?.name || editing) && (
              <section className="xo-report-detail-section">
                <header><h3>{tx("ui.reports.patient_snapshot_543ee362")}</h3><span>{tx("ui.reports.identity_printed_on_this_report_2895c45b")}</span></header>
                {editing ? (
                  <div className="xo-report-edit-grid is-three">
                    <FormField label={tx("ui.reports.name_709a2322")}><input type="text" value={draft.patientName}
                      onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, patientName: event.target.value }))} /></FormField>
                    <FormField label={tx("ui.reports.date_of_birth_9518425f")}><input type="date" value={draft.patientDateOfBirth}
                      onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, patientDateOfBirth: event.target.value }))} /></FormField>
                    <FormField label={tx("ui.reports.sex_e301dd60")}><SelectMenu id="edit-report-gender" value={draft.patientGender}
                      options={genderChoices} placeholder={tx("ui.reports.not_specified_60f1e913")}
                      onChange={(value) => setDraft((currentDraft) => ({ ...currentDraft, patientGender: value }))} /></FormField>
                  </div>
                ) : (
                  <dl className="xo-report-facts is-three">
                    <div><dt>{tx("ui.reports.name_709a2322")}</dt><dd>{current.patient.name}</dd></div>
                    <div><dt>{tx("ui.reports.date_of_birth_9518425f")}</dt><dd>{current.patient.dateOfBirth || '—'}</dd></div>
                    <div><dt>{tx("ui.reports.sex_e301dd60")}</dt><dd>{current.patient.gender ? codeLabel('gender', current.patient.gender) : '—'}</dd></div>
                  </dl>
                )}
              </section>
            )}

            {Array.isArray(current.inputs) && current.inputs.length > 0 && (
              <section className="xo-report-detail-section">
                <header><h3>{tx('ui.reports.request_files')}</h3></header>
                <div className="xo-report-recipient-list">
                  {current.inputs.map((input) => (
                    <span key={input.code}><FileCheck2 /><strong>{input.filename}</strong>
                      <small>{input.label} · {bytes(input.bytes)}</small></span>
                  ))}
                </div>
              </section>
            )}

            {Array.isArray(current.recipients) && current.recipients.length > 0 && (
              <section className="xo-report-detail-section">
                <header><h3>{tx("ui.reports.recipients_78cbf8eb")}</h3><span>{current.recipients.length === 1
                  ? tx('ui.reports.one_email_recipient', { count: current.recipients.length })
                  : tx('ui.reports.many_email_recipients', { count: current.recipients.length })}</span></header>
                <div className="xo-report-recipient-list">
                  {current.recipients.map((recipient) => (
                    <span key={recipient.id}><Mail /><strong>{recipient.email}</strong>
                      <small>{recipient.sentAt === null ? tx("ui.reports.not_sent_587c501e") : tx("ui.reports.sent_value_4b7076d0", { value: when(recipient.sentAt) })}</small></span>
                  ))}
                </div>
              </section>
            )}

            {editing && (
              <div className="xo-report-edit-actions">
                <button type="button" onClick={() => { setEditing(false); setActionError(''); }}>{tx("ui.reports.cancel_77dfd213")}</button>
                {current.status !== 'done' && (
                  <button type="button" disabled={action !== ''} onClick={save}>
                    {action === 'save' ? <Loader2 className="xo-spin" /> : <Save />}
                    {action === 'save' ? tx("ui.reports.saving_56a2285c") : tx("ui.reports.save_changes_179359b3")}
                  </button>
                )}
                {['failed', 'done'].includes(current.status) && (
                  <button type="button" className="is-primary" disabled={action !== ''}
                    onClick={() => rerun(true)}>
                    {action === 'save-rerun' ? <Loader2 className="xo-spin" /> : <RefreshCw />}
                    {action === 'save-rerun' ? tx("ui.reports.queuing_90d650a9") : current.status === 'done' ? tx("ui.reports.generate_updated_report_f8d7ff12") : tx("ui.reports.save_rerun_ac64158d")}
                  </button>
                )}
              </div>
            )}

            {current.status === 'done' && (
              <section className="xo-report-download-card">
                <span aria-hidden="true"><FileCheck2 /></span>
                <div><strong>{tx("ui.reports.pdf_is_ready_9931f42b")}</strong><small>{tx("ui.reports.value_generated_and_secured_in_report_storage_710caf4f", { value: bytes(current.reportBytes) })}</small></div>
                <button type="button" className="xo-btn" disabled={downloading} onClick={download}>
                  {downloading ? <Loader2 className="xo-spin" /> : <Download />}
                  {downloading ? tx("ui.reports.preparing_0b1d6c0b") : tx("ui.reports.download_pdf_98e5ef06")}
                </button>
              </section>
            )}
            {downloadError && <p className="xo-report-inline-error" role="alert">{downloadError}</p>}

            <section className="xo-report-detail-section xo-report-activity-section">
              <header><h3>{tx("ui.reports.activity_81c0d915")}</h3><span>{tx("ui.reports.request_history_f232ba25")}</span></header>
              <div className="xo-report-request-timeline">
                {timeline.map((event, index) => (
                  <article key={event.key} className={index === timeline.length - 1 ? 'is-current' : ''}>
                    <span aria-hidden="true" />
                    <div><strong>{event.label}</strong><small>{event.detail}</small><time>{when(event.at)}</time></div>
                  </article>
                ))}
                {current.status === 'queued' && (
                  <article className="is-current is-waiting"><span aria-hidden="true" />
                    <div><strong>{tx("ui.reports.waiting_for_the_generator_71672dd1")}</strong><small>{tx("ui.reports.the_worker_will_claim_this_automatically_9a446e66")}</small></div>
                  </article>
                )}
                {current.status === 'processing' && (
                  <article className="is-current is-processing"><span aria-hidden="true" />
                    <div><strong>{tx("ui.reports.building_the_pdf_01430df9")}</strong><small>{tx("ui.reports.the_page_will_update_when_the_report_is_secured_b3ea3bd9")}</small></div>
                  </article>
                )}
              </div>
              <dl className="xo-report-request-meta">
                <div><dt>{tx("ui.reports.requested_by_5108c2f7")}</dt><dd>{[current.requestedBy?.firstName, current.requestedBy?.lastName].filter(Boolean).join(' ') || current.requestedBy?.email || '—'}</dd></div>
                <div><dt>{tx("ui.reports.request_id_63aa59d5")}</dt><dd>{current.uuid}</dd></div>
                {current.reportSha256 && <div><dt>{tx("ui.reports.sha_256_45792d6b")}</dt><dd>{current.reportSha256}</dd></div>}
              </dl>
            </section>
          </main>

          <aside className="xo-report-preview-pane">
            <header><div><h3>{tx("ui.reports.report_preview_b38dbc6f")}</h3><span>{current.status === 'done' ? tx("ui.reports.generated_pdf_5dd94e1d") : tx("ui.reports.available_when_processing_completes_cdf4ced3")}</span></div>
              {preview.state === 'ready' && (
                <button type="button" aria-label={tx("ui.reports.open_pdf_in_a_new_tab_f3f9d9b9")} onClick={download}><ExternalLink /></button>
              )}</header>
            <div className="xo-report-preview-frame">
              {preview.state === 'ready' && <iframe src={`${preview.url}#view=FitH`} title={tx("ui.reports.value_pdf_preview_566e2175", { value: current.reportType?.displayName || tx('ui.reports.report_ee45c303') })} />}
              {preview.state === 'loading' && <div><Loader2 className="xo-spin" /><strong>{tx("ui.reports.loading_report_f4c2381a")}</strong><small>{tx("ui.reports.preparing_the_secured_preview_670d1665")}</small></div>}
              {preview.state === 'error' && <div className="is-error"><AlertCircle /><strong>{tx("ui.reports.preview_unavailable_36d31d45")}</strong><small>{preview.message}</small><button type="button" onClick={download}>{tx("ui.reports.open_report_44f83158")}</button></div>}
              {preview.state === 'idle' && <div><FileText /><strong>{tx("ui.reports.no_generated_pdf_yet_938828b5")}</strong><small>{tx("ui.reports.the_preview_will_appear_here_when_this_request_is_read_fd3c453c")}</small></div>}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default function XomicsReports() {
  const loadOptions = useCallback(() => loadReportOptions(), []);
  const optionResource = useLiveResource(['reports', 'pipelines', 'translations'], loadOptions);
  const options = optionResource.data || EMPTY_REPORT_OPTIONS;

  const [wizardStep, setWizardStep] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientTerm, setPatientTerm] = useState('');
  const [patientMatches, setPatientMatches] = useState([]);
  const [patientSearch, setPatientSearch] = useState({ state: 'idle', message: '' });
  const [patientSnapshot, setPatientSnapshot] = useState(EMPTY_PATIENT);
  const [availability, setAvailability] = useState({ state: 'idle', entries: [], message: '' });
  const [availabilityVersion, setAvailabilityVersion] = useState(0);
  const [selectedReports, setSelectedReports] = useState({});
  // The files chosen for each chosen report that asks for one: { "<type>:<job>": { <input code>: file } }, and a
  // problem with one of them under "<type>:<job>:<code>".
  const [reportFiles, setReportFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [companyId, setCompanyId] = useState('');
  const [languageCode, setLanguageCode] = useState('');
  const [recipientText, setRecipientText] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = statusFromParams(searchParams);
  const [requestOffset, setRequestOffset] = useState(0);
  const [requestSearch, setRequestSearch] = useState('');
  const [openRequest, setOpenRequest] = useState(null);

  const patientReportTypes = useMemo(
    () => options.reportTypes.filter((type) => (
      type.pipeline?.isPatient && type.pipeline?.status === 'online'
    )),
    [options.reportTypes],
  );
  const selectedPatientId = selectedPatient?.id ?? null;

  useEffect(() => {
    if (languageCode !== '' || options.languages.length === 0) return;
    const english = options.languages.find((language) => language.code === 'en');
    setLanguageCode(english?.code || options.languages[0].code);
  }, [languageCode, options.languages]);

  useEffect(() => {
    if (companyId !== '' || options.companies.length !== 1) return;
    setCompanyId(String(options.companies[0].id));
  }, [companyId, options.companies]);

  useEffect(() => {
    const query = patientTerm.trim();
    const queryReady = /^\d+$/.test(query)
      ? query.length > 0 : query.length >= PATIENT_SEARCH_MIN;
    if (!queryReady || selectedPatient !== null) {
      setPatientMatches([]);
      setPatientSearch({ state: 'idle', message: '' });
      return undefined;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setPatientSearch({ state: 'loading', message: '' });
      try {
        const result = await searchReportPatients(query);
        if (controller.signal.aborted) return;
        setPatientMatches(result.patients);
        setPatientSearch({
          state: result.patients.length === 0 ? 'empty' : 'ready',
          message: result.patients.length === 0
            ? tx('ui.reports.no_matching_patient_found')
            : result.truncated ? tx('ui.reports.showing_first_matches') : '',
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        setPatientMatches([]);
        setPatientSearch({ state: 'error', message: errorMessage(error) });
      }
    }, PATIENT_SEARCH_DELAY_MS);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [patientTerm, selectedPatient]);

  useEffect(() => {
    if (selectedPatientId === null) {
      setAvailability({ state: 'idle', entries: [], message: '' });
      return undefined;
    }
    if (patientReportTypes.length === 0) {
      setAvailability({ state: 'ready', entries: [], message: '' });
      return undefined;
    }

    let cancelled = false;
    setAvailability({ state: 'loading', entries: [], message: '' });
    Promise.allSettled(patientReportTypes.map(async (type) => ({
      type,
      result: await loadReportJobs({ contentTypeId: type.id, patientId: selectedPatientId }),
    }))).then((results) => {
      if (cancelled) return;
      const entries = results.map((result, index) => {
        const type = patientReportTypes[index];
        if (result.status === 'fulfilled') {
          return { type, jobs: result.value.result.jobs, error: '' };
        }
        return { type, jobs: [], error: errorMessage(result.reason) };
      });
      const failed = entries.filter((entry) => entry.error !== '');
      setAvailability({
        state: failed.length === entries.length ? 'error' : 'ready',
        entries,
        message: failed.length === 0
          ? '' : failed.length === 1
            ? tx('ui.reports.one_report_source_unavailable', { count: failed.length })
            : tx('ui.reports.many_report_sources_unavailable', { count: failed.length }),
      });
    });
    return () => { cancelled = true; };
  }, [availabilityVersion, patientReportTypes, selectedPatientId]);

  const availableSamples = useMemo(() => {
    const samples = new Map();
    availability.entries.forEach((entry) => {
      entry.jobs.forEach((job) => {
        const key = String(job.upload?.id ?? job.upload?.uuid ?? `job-${job.id}`);
        const current = samples.get(key) || {
          key,
          name: job.upload?.sampleTypeName || job.upload?.sampleCode || tx('ui.reports.sample_58fabfa7'),
          visitNumber: job.upload?.visitNumber ?? null,
          completedAt: job.completedAt,
          reportIds: new Set(),
        };
        current.reportIds.add(String(entry.type.id));
        if (Number(job.completedAt) > Number(current.completedAt)) current.completedAt = job.completedAt;
        samples.set(key, current);
      });
    });
    return [...samples.values()]
      .map((sample) => ({ ...sample, reportCount: sample.reportIds.size }))
      .sort((left, right) => Number(right.completedAt || 0) - Number(left.completedAt || 0));
  }, [availability.entries]);

  const selectedReportRows = useMemo(() => availability.entries.flatMap((entry) => (
    selectedIds(selectedReports[String(entry.type.id)]).map((selectedJobId) => ({
      ...entry,
      selectedJobId,
      job: entry.jobs.find((job) => String(job.id) === selectedJobId) || null,
    }))
  )), [availability.entries, selectedReports]);
  const selectedReportsReady = selectedReportRows.length > 0
    && selectedReportRows.every((entry) => entry.job !== null);
  // The chosen reports that ask for files; with any, the files step comes before the delivery.
  const fileRows = selectedReportRows.filter((entry) => reportInputs(entry).length > 0);
  const withFiles = fileRows.length > 0;
  const FILES_STEP = 3;
  const DELIVERY_STEP = withFiles ? 4 : 3;

  async function pickReportFile(entry, input, file) {
    const key = `${reportRowKey(entry)}:${input.code}`;
    try {
      const read = await readReportFile(file, input);
      if (read.problem) {
        setFileErrors((current) => ({ ...current, [key]: read.problem }));
        return;
      }
      setReportFiles((current) => ({
        ...current,
        [reportRowKey(entry)]: { ...(current[reportRowKey(entry)] || {}), [input.code]: read.file },
      }));
      setFileErrors((current) => { const next = { ...current }; delete next[key]; return next; });
      setFormError('');
    } catch (error) {
      setFileErrors((current) => ({ ...current, [key]: errorMessage(error) }));
    }
  }

  function clearReportFile(entry, input) {
    setReportFiles((current) => {
      const next = { ...current, [reportRowKey(entry)]: { ...(current[reportRowKey(entry)] || {}) } };
      delete next[reportRowKey(entry)][input.code];
      return next;
    });
  }

  // What a request sends of its files: only for a report type that asks for any.
  function inputsFor(entry) {
    if (reportInputs(entry).length === 0) return undefined;
    const chosen = reportFiles[reportRowKey(entry)] || {};
    return Object.fromEntries(reportInputs(entry)
      .filter((input) => chosen[input.code])
      .map((input) => [input.code, {
        filename: chosen[input.code].filename,
        mediaType: chosen[input.code].mediaType,
        dataBase64: chosen[input.code].dataBase64,
      }]));
  }

  const loadRequests = useCallback(
    () => loadReportRequests({
      limit: REPORT_REQUESTS_PAGE_LIMIT,
      offset: requestOffset,
      status: statusFilter,
    }),
    [requestOffset, statusFilter],
  );
  const requestResource = useLiveResource(['reports'], loadRequests, { reconcileMs: 15000 });
  const requestData = requestResource.data || EMPTY_REPORT_REQUESTS;
  const counts = requestData.statusCounts;
  const allCount = Object.values(counts).reduce((total, value) => total + Number(value || 0), 0);
  const visibleRequests = useMemo(() => {
    const needle = requestSearch.trim().toLowerCase();
    if (needle === '') return requestData.reportRequests;
    return requestData.reportRequests.filter((request) => [
      request.uuid, request.jobId, request.patient?.name, request.company?.name,
      request.reportType?.displayName, request.language?.name, request.language?.nativeName,
    ].filter(Boolean).join(' ').toLowerCase().includes(needle));
  }, [requestData.reportRequests, requestSearch]);

  // Keep an open request in step with the live list. Without this, a modal
  // opened while processing would stay frozen even after the socket delivered
  // the completion event and the rail changed to Ready.
  useEffect(() => {
    if (openRequest === null) return;
    const latest = requestData.reportRequests.find((request) => request.uuid === openRequest.uuid);
    if (latest && latest !== openRequest) setOpenRequest(latest);
  }, [openRequest, requestData.reportRequests]);

  function choosePatient(patient) {
    setSelectedPatient(patient);
    setPatientTerm('');
    setPatientMatches([]);
    setPatientSearch({ state: 'idle', message: '' });
    setPatientSnapshot({
      name: patient.name || '',
      dateOfBirth: patient.dateOfBirth || '',
      gender: patient.gender || '',
    });
    setSelectedReports({});
    setReportFiles({});
    setFileErrors({});
    setWizardStep(1);
    setFormError('');
    setSuccess('');
    setFieldErrors((current) => ({ ...current, patientId: undefined }));
  }

  function clearPatient() {
    setSelectedPatient(null);
    setPatientTerm('');
    setPatientSnapshot(EMPTY_PATIENT);
    setSelectedReports({});
    setReportFiles({});
    setFileErrors({});
    setWizardStep(1);
    setFieldErrors({});
    setFormError('');
    setSuccess('');
  }

  function toggleReportJob(contentTypeId, jobId) {
    const key = String(contentTypeId);
    const selectedJobId = String(jobId);
    setSelectedReports((current) => {
      const next = { ...current };
      const currentIds = selectedIds(current[key]);
      const nextIds = currentIds.includes(selectedJobId)
        ? currentIds.filter((id) => id !== selectedJobId)
        : [...currentIds, selectedJobId];
      if (nextIds.length === 0) delete next[key];
      else next[key] = nextIds;
      return next;
    });
    setFieldErrors((current) => ({ ...current, reports: undefined }));
    setFormError('');
  }

  function continueFromPatient() {
    const errors = {};
    if (selectedPatient === null) errors.patientId = tx("ui.reports.choose_a_patient_first_b39fae54");
    if (patientSnapshot.name.trim() === '') errors.patientName = tx("ui.reports.a_patient_name_is_required_a194d3a1");
    if (availability.state === 'loading') errors.samples = tx("ui.reports.wait_while_the_available_samples_are_checked_e9040936");
    if (availability.state === 'error') errors.samples = availability.message || tx("ui.reports.samples_could_not_be_checked_a578d8be");
    if (availability.state === 'ready' && availableSamples.length === 0) {
      errors.samples = tx("ui.reports.this_patient_has_no_completed_analyses_that_can_produc_d523fec5");
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError(tx("ui.reports.please_finish_the_patient_and_sample_step_2848b4ea"));
      return;
    }
    setFieldErrors({});
    setFormError('');
    setSuccess('');
    setWizardStep(2);
  }

  function continueFromReports() {
    if (selectedReportRows.length === 0) {
      setFieldErrors({ reports: tx('ui.reports.choose_at_least_one_report') });
      setFormError(tx("ui.reports.choose_the_reports_you_want_to_generate_5632899d"));
      return;
    }
    if (!selectedReportsReady) {
      setFieldErrors({ reports: tx('ui.reports.choose_at_least_one_compatible_visit_284dad7f') });
      setFormError(tx("ui.reports.each_report_request_needs_a_compatible_sample_and_visi_bba59685"));
      return;
    }
    setFieldErrors({});
    setFormError('');
    setSuccess('');
    // The files step when a chosen report asks for files, else the delivery: step 3 either way.
    setWizardStep(withFiles ? FILES_STEP : DELIVERY_STEP);
  }

  // Every file a chosen report requires is there; then the delivery.
  function continueFromFiles() {
    const missing = {};
    for (const entry of fileRows) {
      const chosen = reportFiles[reportRowKey(entry)] || {};
      for (const input of reportInputs(entry)) {
        if (input.required && !chosen[input.code]) {
          missing[`${reportRowKey(entry)}:${input.code}`] = tx('ui.reports.file_missing', { name: input.label });
        }
      }
    }
    if (Object.keys(missing).length > 0) {
      setFileErrors((current) => ({ ...current, ...missing }));
      setFormError(tx('ui.reports.files_missing'));
      return;
    }
    setFormError('');
    setSuccess('');
    setWizardStep(DELIVERY_STEP);
  }

  // A new status (a tab click or a dashboard link) starts the list from its first page.
  useEffect(() => { setRequestOffset(0); }, [statusFilter]);

  function changeStatus(next) {
    const nextParams = new URLSearchParams(searchParams);
    if (next === 'all') nextParams.delete('status');
    else nextParams.set('status', next);
    setSearchParams(nextParams, { replace: true });
    setRequestOffset(0);
  }

  async function submit(event) {
    event.preventDefault();
    if (wizardStep === 1) {
      continueFromPatient();
      return;
    }
    if (wizardStep === 2) {
      continueFromReports();
      return;
    }
    if (withFiles && wizardStep === FILES_STEP) {
      continueFromFiles();
      return;
    }

    const errors = {};
    if (selectedPatient === null) errors.patientId = tx("ui.reports.choose_a_patient_402f6f55");
    if (!selectedReportsReady) errors.reports = tx("ui.reports.choose_at_least_one_compatible_visit_284dad7f");
    if (companyId === '') errors.companyId = tx("ui.reports.choose_the_company_branding_49b5e25d");
    if (languageCode === '') errors.languageCode = tx("ui.reports.choose_a_language_a5176438");
    if (patientSnapshot.name.trim() === '') errors.patientName = tx("ui.reports.a_patient_name_is_required_a194d3a1");
    const recipients = parseRecipients(recipientText);
    const invalidEmail = recipients.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    if (invalidEmail) errors.recipients = tx("ui.reports.invalidemail_is_not_a_valid_email_address_a84f27ad", { invalidEmail: invalidEmail });
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError(tx("ui.reports.please_finish_the_highlighted_details_8a871cbe"));
      return;
    }

    setSubmitting(true);
    setFieldErrors({});
    setFormError('');
    setSuccess('');
    try {
      const results = await Promise.allSettled(selectedReportRows.map((entry) => (
        submitReportRequest({
          jobId: entry.job.id,
          patientId: selectedPatient.id,
          contentTypeId: entry.type.id,
          companyId: Number(companyId),
          languageCode,
          patientName: patientSnapshot.name.trim(),
          patientDateOfBirth: patientSnapshot.dateOfBirth || null,
          patientGender: patientSnapshot.gender || null,
          recipients,
          // the report's own files, only for a type that asks for any
          ...(inputsFor(entry) ? { inputs: inputsFor(entry) } : {}),
        })
      )));
      const succeeded = [];
      const failed = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') succeeded.push(selectedReportRows[index]);
        else failed.push({ entry: selectedReportRows[index], error: result.reason });
      });

      if (succeeded.length > 0) {
        setSuccess(succeeded.length === 1
          ? tx('ui.reports.one_report_added_to_queue', { count: succeeded.length })
          : tx('ui.reports.many_reports_added_to_queue', { count: succeeded.length }));
        setRecipientText('');
        await requestResource.refresh();
        setAvailabilityVersion((current) => current + 1);
      }
      if (failed.length > 0) {
        const retained = {};
        failed.forEach(({ entry }) => {
          const key = String(entry.type.id);
          retained[key] = [...(retained[key] || []), String(entry.selectedJobId)];
        });
        setSelectedReports(retained);
        const failures = failed.map(({ entry, error }) => `${entry.type.displayName} (${errorMessage(error)})`).join('; ');
        setFormError(failed.length === 1
          ? tx('ui.reports.one_report_could_not_be_queued', { failures })
          : tx('ui.reports.many_reports_could_not_be_queued', { count: failed.length, failures }));
      } else {
        setSelectedReports({});
        setReportFiles({});
        setFileErrors({});
        setSelectedPatient(null);
        setPatientTerm('');
        setPatientMatches([]);
        setPatientSearch({ state: 'idle', message: '' });
        setPatientSnapshot(EMPTY_PATIENT);
        setFieldErrors({});
        setWizardStep(1);
      }
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setSubmitting(false);
    }
  }

  const companyChoices = options.companies.map((company) => ({
    value: company.id, label: company.name, detail: company.code,
  }));
  const languageChoices = options.languages.map((language) => ({
    value: language.code,
    label: language.nativeName || language.name,
    detail: language.nativeName && language.nativeName !== language.name ? language.name : language.code.toUpperCase(),
  }));
  const genderChoices = [
    { value: 'male', label: tx("ui.reports.male_3f3a489c") },
    { value: 'female', label: tx("ui.reports.female_b7c17e97") },
    { value: 'other', label: tx("ui.reports.other_6e6a6f20") },
  ];
  const pageFrom = requestData.total === 0 ? 0 : requestOffset + 1;
  const pageTo = Math.min(requestOffset + requestData.reportRequests.length, requestData.total);

  return (
    <div className="xo-report-board">
      <main className="xo-reports-main">
        <Head title={tx("ui.reports.reports_88bc3fe3")} subtitle={tx("ui.reports.build_a_report_from_a_completed_pipeline_job_and_follo_56f6c8f5")} />

        <section className="xo-report-journey-shell">
          {optionResource.loading && optionResource.data === null && <Loading label={tx("ui.reports.loading_report_options_f506eb5d")} />}
          {optionResource.error && optionResource.data === null && (
            <Failed message={optionResource.error} onRetry={optionResource.refresh} />
          )}

          {optionResource.data !== null && (
            <form onSubmit={submit} className="xo-report-form xo-report-journey" noValidate>
              <ReportWizardSteps step={wizardStep} withFiles={withFiles} />
              {formError && (
                <p className="xo-report-notice is-error" role="alert">
                  <AlertCircle aria-hidden="true" /><span>{formError}</span>
                  <button type="button" aria-label={tx("ui.reports.dismiss_error_347aaa77")} onClick={() => setFormError('')}><X /></button>
                </p>
              )}
              {success && (
                <p className="xo-report-notice is-success" role="status">
                  <CheckCircle2 aria-hidden="true" /><span>{success}</span>
                  <button type="button" aria-label={tx("ui.reports.dismiss_message_98f3b0e0")} onClick={() => setSuccess('')}><X /></button>
                </p>
              )}

              {wizardStep === 1 && (
                <section className={`xo-report-wizard-stage xo-report-patient-stage${selectedPatient === null ? ' is-empty' : ''}`}>
                  <header className="xo-report-stage-heading">
                    <span aria-hidden="true"><UserRound /></span>
                    <div><h3>{selectedPatient === null ? tx("ui.reports.choose_the_patient_8bd19775") : tx("ui.reports.patient_details_and_samples_ae37010e")}</h3></div>
                  </header>
                  <div className="xo-report-stage-body">
                    <PatientPicker patient={selectedPatient} term={patientTerm} setTerm={setPatientTerm}
                      matches={patientMatches} state={patientSearch.state} message={patientSearch.message}
                      onChoose={choosePatient} onClear={clearPatient} />
                    {fieldErrors.patientId && <small className="xo-report-stage-error">{fieldErrors.patientId}</small>}

                    {selectedPatient !== null && (
                      <>
                        <div className="xo-report-snapshot-grid xo-report-patient-fields">
                          <FormField label={tx("ui.reports.name_709a2322")} error={fieldErrors.patientName}>
                            <input type="text" value={patientSnapshot.name}
                              onChange={(event) => setPatientSnapshot((current) => ({ ...current, name: event.target.value }))} />
                          </FormField>
                          <FormField label={tx("ui.reports.date_of_birth_9518425f")} error={fieldErrors.patientDateOfBirth}>
                            <input type="date" value={patientSnapshot.dateOfBirth}
                              onChange={(event) => setPatientSnapshot((current) => ({ ...current, dateOfBirth: event.target.value }))} />
                          </FormField>
                          <FormField label={tx("ui.reports.sex_e301dd60")} error={fieldErrors.patientGender}>
                            <SelectMenu id="report-gender" value={patientSnapshot.gender} options={genderChoices}
                              placeholder={tx("ui.reports.not_recorded_305cc364")} onChange={(value) => setPatientSnapshot((current) => ({ ...current, gender: String(value) }))} />
                          </FormField>
                        </div>
                        {availability.state === 'ready' && availability.message && (
                          <p className="xo-report-availability-warning"><AlertCircle />{availability.message}</p>
                        )}
                        <SampleInventory samples={availableSamples}
                          loading={availability.state === 'loading'}
                          error={availability.state === 'error' ? availability.message : ''}
                          onRetry={() => setAvailabilityVersion((current) => current + 1)} />
                        {fieldErrors.samples && <small className="xo-report-stage-error">{fieldErrors.samples}</small>}
                      </>
                    )}
                  </div>
                </section>
              )}

              {wizardStep === 2 && (
                <section className="xo-report-wizard-stage">
                  <header className="xo-report-stage-heading">
                    <span aria-hidden="true"><FileText /></span>
                    <div><h3>{tx("ui.reports.choose_reports_and_visits_8b10bea7")}</h3></div>
                  </header>
                  <div className="xo-report-stage-body">
                    <div className="xo-report-stage-context">
                      <UserRound aria-hidden="true" />
                      <div><strong>{patientSnapshot.name || selectedPatient?.name}</strong>
                        <small>{availableSamples.length === 1
                          ? tx('ui.reports.patient_with_one_available_sample', { patient: selectedPatient?.id, count: availableSamples.length })
                          : tx('ui.reports.patient_with_many_available_samples', { patient: selectedPatient?.id, count: availableSamples.length })}</small></div>
                      <button type="button" onClick={() => setWizardStep(1)}>{tx("ui.reports.edit_patient_9fde633d")}</button>
                    </div>
                    <div className={`xo-report-choice-list${fieldErrors.reports ? ' is-error' : ''}`}>
                      {availability.entries.map((entry) => (
                        <ReportChoice key={entry.type.id} entry={entry}
                          selectedJobIds={selectedIds(selectedReports[String(entry.type.id)])}
                          onToggleJob={(jobId) => toggleReportJob(entry.type.id, jobId)} />
                      ))}
                    </div>
                    {fieldErrors.reports && <small className="xo-report-stage-error">{fieldErrors.reports}</small>}
                  </div>
                </section>
              )}

              {withFiles && wizardStep === FILES_STEP && (
                <section className="xo-report-wizard-stage">
                  <header className="xo-report-stage-heading">
                    <span aria-hidden="true"><FileCheck2 /></span>
                    <div><h3>{tx('ui.reports.files_heading')}</h3></div>
                  </header>
                  <div className="xo-report-stage-body">
                    <p className="xo-report-files-intro">{tx('ui.reports.files_intro')}</p>
                    <div className="xo-report-file-list">
                      {fileRows.map((entry) => (
                        <article key={reportRowKey(entry)} className="xo-report-file-card">
                          <header>
                            <span aria-hidden="true"><FileText /></span>
                            <div><strong>{entry.type.displayName}</strong>
                              <small>{entry.job?.upload?.sampleTypeName || entry.job?.upload?.sampleCode || tx("ui.reports.sample_58fabfa7")}
                                {' · '}{entry.job?.upload?.visitNumber === null ? tx("ui.reports.visit_not_recorded_c97e50d9") : tx("ui.reports.visit_value_4180c67c", { value: entry.job?.upload?.visitNumber })}
                                {' · '}{tx("ui.reports.job_63f76aa3")}{entry.job?.id}</small></div>
                          </header>
                          {reportInputs(entry).map((input) => (
                            <ReportFileDrop key={input.code} input={input}
                              file={(reportFiles[reportRowKey(entry)] || {})[input.code] || null}
                              error={fileErrors[`${reportRowKey(entry)}:${input.code}`] || ''}
                              onPick={(file) => pickReportFile(entry, input, file)}
                              onClear={() => clearReportFile(entry, input)} />
                          ))}
                        </article>
                      ))}
                    </div>
                  </div>
                </section>
              )}

              {wizardStep === DELIVERY_STEP && (
                <section className="xo-report-wizard-stage">
                  <header className="xo-report-stage-heading">
                    <span aria-hidden="true"><Send /></span>
                    <div><h3>{tx("ui.reports.presentation_and_delivery_fbf6b4b9")}</h3></div>
                  </header>
                  <div className="xo-report-stage-body">
                    <section className="xo-report-batch-summary">
                      <header><div><h3>{selectedReportRows.length === 1
                        ? tx('ui.reports.one_selected_report', { count: selectedReportRows.length })
                        : tx('ui.reports.many_selected_reports', { count: selectedReportRows.length })}</h3>
                        <p>{tx("ui.reports.name_each_report_is_tied_to_the_source_below_aa80b9fa", { name: patientSnapshot.name })}</p></div></header>
                      <div>
                        {selectedReportRows.map((entry) => (
                          <article key={`${entry.type.id}:${entry.selectedJobId}`}>
                            <span aria-hidden="true"><FileCheck2 /></span>
                            <div><strong>{entry.type.displayName}</strong>
                              <small>{entry.job?.upload?.sampleTypeName || entry.job?.upload?.sampleCode || tx("ui.reports.sample_58fabfa7")}
                                {' · '}{entry.job?.upload?.visitNumber === null ? tx("ui.reports.visit_not_recorded_c97e50d9") : tx("ui.reports.visit_value_4180c67c", { value: entry.job?.upload?.visitNumber })}
                                {' · '}{tx("ui.reports.job_63f76aa3")}{entry.job?.id}</small>
                              {reportInputs(entry).filter((input) => (reportFiles[reportRowKey(entry)] || {})[input.code]).map((input) => (
                                <small key={input.code} className="xo-report-file-line">
                                  {tx('ui.reports.file_attached', { name: input.label, filename: reportFiles[reportRowKey(entry)][input.code].filename })}
                                </small>
                              ))}</div>
                          </article>
                        ))}
                      </div>
                    </section>

                    <div className="xo-report-form-grid">
                      <FormField label={tx("ui.reports.company_7a199499")} error={fieldErrors.companyId}>
                        <SelectMenu id="report-company" value={companyId} options={companyChoices}
                          placeholder={tx("ui.reports.choose_company_14b2f441")} onChange={(value) => setCompanyId(String(value))} />
                      </FormField>
                      <FormField label={tx("ui.reports.language_89b86ab0")} error={fieldErrors.languageCode}>
                        <SelectMenu id="report-language" value={languageCode} options={languageChoices}
                          placeholder={tx("ui.reports.choose_language_94d0c90d")} onChange={(value) => setLanguageCode(String(value))} />
                      </FormField>
                    </div>

                    <FormField label={tx("ui.reports.email_recipients_c75f86e4")} error={fieldErrors.recipients}
                      hint={tx("ui.reports.optional_separate_multiple_addresses_with_a_comma_or_s_517690ed")}>
                      <div className="xo-report-email-input"><Mail aria-hidden="true" />
                        <input type="text" value={recipientText} placeholder={tx("ui.reports.clinician_example_com_3b0f8f45")}
                          autoComplete="off" onChange={(event) => setRecipientText(event.target.value)} /></div>
                    </FormField>
                  </div>
                </section>
              )}

              <footer className="xo-report-wizard-footer">
                <div className="xo-report-footer-actions">
                  {wizardStep > 1 && (
                    <button type="button" className="xo-report-back" disabled={submitting}
                      onClick={() => { setWizardStep((current) => current - 1); setFormError(''); }}>
                      <ChevronLeft />{tx("ui.reports.back_b52b36b7")}
                    </button>
                  )}
                  <button type="submit" className="xo-btn" disabled={submitting
                    || (wizardStep === 1 && (selectedPatient === null || availability.state === 'loading'))}>
                    {submitting ? <Loader2 className="xo-spin" /> : wizardStep === DELIVERY_STEP ? <FileCheck2 /> : null}
                    {submitting ? tx("ui.reports.adding_to_queue_8abd8264") : wizardStep === 1 ? tx("ui.reports.continue_to_reports_5bc2c101")
                      : wizardStep === 2 && withFiles ? tx('ui.reports.continue_to_files')
                        : wizardStep < DELIVERY_STEP ? tx("ui.reports.continue_to_delivery_7ae63e1d")
                        : selectedReportRows.length === 1
                          ? tx('ui.reports.generate_one_report', { count: selectedReportRows.length })
                          : tx('ui.reports.generate_many_reports', { count: selectedReportRows.length })}
                    {!submitting && wizardStep < DELIVERY_STEP && <ChevronRight />}
                  </button>
                </div>
              </footer>
            </form>
          )}
        </section>
      </main>

      <aside className="xo-report-rail">
        <RailHeader title={tx("ui.reports.report_activity_f00976e2")} />
        <WorkspaceTabs className="xo-report-status-tabs is-rail" value={statusFilter}
          label={tx("ui.reports.report_status_4f513b8f")} onChange={changeStatus} scrollable
          tabs={STATUS_TABS.map((tab) => ({
            ...tab,
            label: tx(tab.labelKey),
            count: tab.value === 'all' ? allCount : Number(counts[tab.value] || 0),
          }))} />
        <div className="xo-workspace-search">
          <SearchField value={requestSearch} onChange={setRequestSearch}
            placeholder={tx("ui.reports.search_reports_f2846eef")} />
        </div>

        <div className="xo-report-request-list">
          {requestResource.loading && requestResource.data === null && <Loading label={tx("ui.reports.reading_report_activity_8b63587b")} />}
          {requestResource.error && requestResource.data === null && <Failed message={requestResource.error} onRetry={requestResource.refresh} />}
          {requestResource.data !== null && visibleRequests.length === 0 && (
            <Empty title={requestSearch ? tx("ui.reports.no_matching_reports_a0fd8300") : tx('ui.reports.no_reports_in_view')}
              detail={requestSearch ? tx("ui.reports.try_a_patient_report_company_job_or_request_id_8658a656") : tx("ui.reports.new_report_requests_will_appear_here_automatically_0b9ce92c")} />
          )}
          {visibleRequests.map((request) => (
            <RequestCard key={request.uuid} request={request} onOpen={() => setOpenRequest(request)} />
          ))}
        </div>

        <footer>
          <span>{tx("ui.reports.showing_pagefrom_pageto_of_total_00420119", { pageFrom: pageFrom, pageTo: pageTo, total: requestData.total })}</span>
          <div>
            <button type="button" aria-label={tx("ui.reports.previous_report_page_ff45eff6")}
              disabled={requestResource.loading || requestOffset === 0}
              onClick={() => setRequestOffset(Math.max(0, requestOffset - REPORT_REQUESTS_PAGE_LIMIT))}><ChevronLeft /></button>
            <button type="button" aria-label={tx("ui.reports.next_report_page_ed5ff490")}
              disabled={requestResource.loading || pageTo >= requestData.total}
              onClick={() => setRequestOffset(requestOffset + REPORT_REQUESTS_PAGE_LIMIT)}><ChevronRight /></button>
          </div>
        </footer>
      </aside>

      {openRequest !== null && (
        <RequestDetailModal request={openRequest} options={options}
          onChanged={(updated) => { setOpenRequest(updated); requestResource.refresh(); }}
          onClose={() => setOpenRequest(null)} />
      )}
    </div>
  );
}
